import { get, omit } from "lodash-es";
import objectHash from "object-hash";
import posthog from "posthog-js";
import React, { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";

import BeforeBackActionDialog from "../../components/BeforeBackActionDialog";
import Header from "../../components/Header";
import NamespaceRoleGuard from "../../components/NamespaceRoleGuard";
import Page from "../../components/Page";
import StickyEditBar from "../../components/StickyEditBar";
import {
  EditIcon,
  LoadBalancerIcon,
  OutlinedSettingsIcon,
  RouteIcon,
} from "../../components/icons";
import Tab from "../../components/styled/Tab";
import TabSection from "../../components/styled/TabSection";
import useNavigateWithSearch from "../../hooks/use-navigate-with-search";
import { usePolicyRestrictedInHostedCluster } from "../../hooks/use-policy-restricted-in-hosted-cluster";
import useReadOnly from "../../hooks/use-readonly";
import { HELP_DOCS_DEFAULT_BASE_URL } from "../../slices/help_docs";
import {
  clearForm,
  clearPolicyHash,
  setPolicyForm,
} from "../../slices/policies";
import { setBannerMessage } from "../../slices/pricing_info";
import { useDispatch, useSelector } from "../../store";
import {
  Policy,
  useCreatePolicyMutation,
  useGetPolicyQuery,
  useListDefaultTemplatesQuery,
  useUpdatePolicyMutation,
} from "../../store/pomeriumApi";
import Advanced from "./components/Advanced";
import AttachedRoutes from "./components/AttachedRoutes";
import GeneralPolicyConfiguration from "./components/GeneralPolicyConfiguration";
import PPLEditor from "./components/PPLEditor";
import {
  captureProductEvent,
  productAnalyticsEvents,
} from "@/analytics/events";

interface Props {
  policyId?: string;
}

const Edit: FC<React.PropsWithChildren<Props>> = () => {
  const dispatch = useDispatch();
  const [showBackWarning, setShowBackWarning] = useState(false);
  const { form, policyHash } = useSelector((state) => state.policies);
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const { policyId } = useParams();
  const [saving, setSaving] = useState(false);
  const [addPolicy] = useCreatePolicyMutation();
  const [updatePolicy] = useUpdatePolicyMutation();

  const { data: defaultPolicy, isLoading: defaultLoading } =
    useListDefaultTemplatesQuery("policy", { skip: !!policyId });

  const { data: policy, isLoading } = useGetPolicyQuery(
    {
      organizationId,
      policyId: policyId || "",
    },
    { skip: !organizationId || !policyId },
  );
  const readonly = useReadOnly({ clusterId, organizationId });
  const isPolicyRestricted = usePolicyRestrictedInHostedCluster();
  const navigate = useNavigateWithSearch();
  const [tabValue, setTabValue] = React.useState("general");
  const handleChange = (
    _event: any,
    newValue: React.SetStateAction<string>,
  ) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (!policyId && defaultPolicy) {
      dispatch(
        setPolicyForm(get(defaultPolicy, "0.defaultProperties", {}) as Policy),
      );
    }
  }, [policyId, defaultLoading]);

  useEffect(() => {
    if (!policyId) {
      captureProductEvent(productAnalyticsEvents.policyCreateStarted);
      posthog.capture("new_policy_started");
      dispatch(clearPolicyHash());
    } else if (policy) {
      dispatch(setPolicyForm(policy));
      posthog.capture("policy_edited");
    }
  }, [policyId, isLoading]);

  useEffect((): (() => void) => {
    //dispatch(disableNamespaceSelector());
    return (): void => {
      dispatch(clearForm());
    };
  }, []);

  const handleSubmit = async () => {
    if (isPolicyRestricted) {
      dispatch(
        setBannerMessage(
          "To create or edit policies in hosted clusters, upgrade your account to Professional.",
        ),
      );
      return;
    }

    setSaving(true);
    try {
      if (policyId) {
        await updatePolicy({
          organizationId,
          policyId,
          policyProperties: omit(form, ["updatedAt", "createdAt"]) as Policy,
        });
        posthog.capture("policy_saved");
      } else {
        await addPolicy({
          organizationId,
          policyProperties: { ...form, namespaceId: clusterId } as Policy,
        });
        posthog.capture("new_policy_created");
      }
      navigate("/app/management/policies", { replace: true });
    } catch {
      // handled in ErrorMiddleware.ts
    } finally {
      setSaving(false);
    }
  };

  const handleClickBack = (): void => {
    if (policyHash !== objectHash(form)) {
      setShowBackWarning(true);
    } else {
      navigate("/app/management/policies", { replace: true });
    }
  };
  const shouldSave = (): boolean => {
    return objectHash(form) !== policyHash && !saving && !!form?.name;
  };

  if (isLoading) {
    return <></>;
  }

  if (policyId && !form?.id) {
    return (
      <Box display="flex" sx={{ m: 10 }}>
        <Alert severity="warning">
          The policy does not exist and may have been deleted!
        </Alert>
      </Box>
    );
  }

  const title = policyId
    ? `Edit Policy ${policy?.name || ""}`
    : "Create Policy";

  return (
    <Page title={title}>
      <BeforeBackActionDialog
        open={showBackWarning}
        onClose={() => setShowBackWarning(false)}
        navPath="/app/management/policies"
      />
      <Header
        breadcrumbs={true}
        title={title}
        helpUrl={HELP_DOCS_DEFAULT_BASE_URL + "/docs/capabilities/ppl"}
        videoId="lRpkd20psbk"
        originatorId={policy?.originatorId}
      />
      <NamespaceRoleGuard namespaceId={clusterId} role={"viewer"} warn={true}>
        <form autoComplete="off" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid>
              <Box style={{ position: "sticky", top: 0 }}>
                <Tabs
                  aria-labelledby={"policyEditVerticalFormSectionTabs"}
                  orientation="vertical"
                  onChange={handleChange}
                  indicatorColor="primary"
                  textColor="primary"
                  selectionFollowsFocus={true}
                  value={tabValue}
                >
                  <Tab
                    label="General"
                    icon={
                      <OutlinedSettingsIcon color="inherit" sx={{ pr: 1 }} />
                    }
                    iconPosition="start"
                    aria-controls="tabpanel-general"
                    id="tab-general"
                    disableFocusRipple={true}
                    disableRipple={true}
                    value="general"
                  />
                  <Tab
                    label="PPL Editor"
                    value="editor"
                    icon={<EditIcon color="inherit" sx={{ pr: 1 }} />}
                    iconPosition="start"
                    aria-controls="tabpanel-editor"
                    id="tab-editor"
                    disableFocusRipple={true}
                    disableRipple={true}
                  />
                  {!!policyId && (
                    <Tab
                      label={
                        policy?.enforced ? "Enforced Routes" : "Attached Routes"
                      }
                      value="attached"
                      icon={<RouteIcon color="inherit" sx={{ pr: 1 }} />}
                      iconPosition="start"
                      aria-controls="tabpanel-editor"
                      id="tab-editor"
                      disableFocusRipple={true}
                      disableRipple={true}
                    />
                  )}
                  <Tab
                    label="Advanced"
                    icon={<LoadBalancerIcon color="inherit" sx={{ pr: 1 }} />}
                    iconPosition="start"
                    aria-controls="tabpanel-advanced"
                    id="tab-advanced"
                    disableFocusRipple={true}
                    value={"advanced"}
                  />
                </Tabs>
              </Box>
            </Grid>
            <Grid size="grow">
              <Container
                component={Paper}
                maxWidth={false}
                disableGutters={true}
              >
                <TabSection index={"general"} value={tabValue} title="General">
                  <GeneralPolicyConfiguration
                    readonly={readonly}
                    policyId={policyId || ""}
                    isCurrentTab={tabValue === "general"}
                  />
                </TabSection>
                <TabSection
                  value={tabValue}
                  index="editor"
                  title="Policy Editor"
                >
                  <PPLEditor
                    readonly={readonly}
                    isCurrentTab={tabValue === "editor"}
                  />
                </TabSection>
                {!!policyId && (
                  <TabSection
                    value={tabValue}
                    index="attached"
                    title={
                      policy?.enforced ? "Enforced Routes" : "Attached Routes"
                    }
                  >
                    <AttachedRoutes
                      routes={policy?.routes || []}
                      enforcedRoutes={policy?.enforcedRoutes || []}
                    />
                  </TabSection>
                )}
                <TabSection
                  index={"advanced"}
                  value={tabValue}
                  title="Advanced"
                >
                  <Advanced readonly={readonly} />
                </TabSection>
              </Container>
            </Grid>
          </Grid>
          <StickyEditBar
            name="Policy"
            allowCancel={true}
            cancelType={"Back"}
            allowSave={shouldSave()}
            onCancel={(): void => handleClickBack()}
            onSave={handleSubmit}
          />
        </form>
      </NamespaceRoleGuard>
    </Page>
  );
};
export default Edit;
